import { Alert, Box } from '@mui/material';
import QcPageLayout from '../components/QcPageLayout';
import {
  QC_TASK_PAGE_ID,
  QcTaskAssignmentsSummary,
  QcTaskTable,
  QcTaskWorkflowBatchFilters,
  useQcTaskPageController,
} from './mt-qc-task-001';

/**
 * MT-QC-TASK-001 — “My QC Task”: workflows, batches, and assigned requests with server pagination.
 *
 * Feature code lives under `./mt-qc-task-001/` (api, hooks, components, table, utils, styles).
 * Shared QC utilities: `../shared/utils/`.
 */
export default function MyQcTaskPage() {
  const {
    activeTab,
    workflowId,
    batchId,
    workflowValue,
    batchValue,
    workflowOptions,
    batchOptions,
    handleTabChange,
    handleWorkflowChange,
    handleBatchChange,
    isWorkflowLoading,
    isWorkflowFetching,
    isBatchesLoading,
    isBatchesFetching,
    rows,
    rowCount,
    tableLoading,
    pagination,
    setPagination,
    refetchAssignments,
    pageSizeOptions,
    defaultPageSize,
    errorMessage,
    tableFilters,
    applyTableFiltersToUrl,
    sorting,
    onSortingChange,
  } = useQcTaskPageController();

  return (
    <QcPageLayout
      title="QC Task"
      metaDescription="Your assigned QC tasks in QC Lite."
      breadcrumbTitle="QC Task"
      breadcrumbSubtitle={`Page ID: ${QC_TASK_PAGE_ID}`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0.5, sm: 1, md: 1.5, lg: 2, xl: 2.5 },
          // Extra bottom space on phones when browser chrome / home indicator eats viewport.
          pb: { xs: 'max(4px, env(safe-area-inset-bottom, 0px))', sm: 0 },
        }}
      >
        <QcTaskWorkflowBatchFilters
          activeTab={activeTab}
          workflowId={workflowId}
          workflowValue={workflowValue}
          batchValue={batchValue}
          workflowOptions={workflowOptions}
          batchOptions={batchOptions}
          isWorkflowLoading={isWorkflowLoading}
          isWorkflowFetching={isWorkflowFetching}
          isBatchesLoading={isBatchesLoading}
          isBatchesFetching={isBatchesFetching}
          onWorkflowChange={handleWorkflowChange}
          onBatchChange={handleBatchChange}
        />

        {errorMessage ? (
          <Alert
            severity="error"
            sx={{
              py: { xs: 0.35, sm: 0.75 },
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
              '& .MuiAlert-icon': { fontSize: { xs: '1.1rem', sm: '1.25rem' } },
            }}
          >
            {errorMessage}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            // Tight stack: “Showing …” should sit close to the table (parent column gap stays for filters).
            gap: { xs: 0, sm: 0.125, md: 0.25 },
          }}
        >
          <QcTaskAssignmentsSummary
            rowsLength={rows.length}
            rowCount={rowCount}
            loading={tableLoading}
          />

          <QcTaskTable
            key={`qc-table-${activeTab}-${workflowId}-${batchId}`}
            data={rows}
            pageWorkflowId={workflowId}
            pageSizeOptions={pageSizeOptions}
            defaultPageSize={defaultPageSize}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            pagination={pagination}
            onPaginationChange={setPagination}
            onRefreshTable={refetchAssignments}
            rowCount={rowCount}
            isLoading={tableLoading}
            tableFilters={tableFilters}
            onTableFiltersChange={applyTableFiltersToUrl}
            sorting={sorting}
            onSortingChange={onSortingChange}
          />
        </Box>
      </Box>
    </QcPageLayout>
  );
}
